/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { AxiosResponse } from 'axios';
import { UseFormReturn } from 'react-hook-form';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { PagedList, ServiceResponse } from 'interfaces/general';
import { IParamsFilter } from 'api/catalog';
import { useGlobalContext } from 'context/GlobalContext';
import {
  WarehouseSearchParams,
  WorkForcesKITSInventoryParams
} from 'api/inventory';

import usePagination from './usePagination';

export type UseCatalogParams = IParamsFilter &
  WorkForcesKITSInventoryParams &
  WarehouseSearchParams;

interface Props {
  enabled?: boolean;
  getKey: () => string;
  getFunction?: (
    data: IParamsFilter
  ) => Promise<Partial<AxiosResponse<ServiceResponse<PagedList<any>>>>>;
  createFunction?: (
    data: any
  ) => Promise<Partial<AxiosResponse<ServiceResponse<any>>>>;
  updateFunction?: (
    data: any
  ) => Promise<Partial<AxiosResponse<ServiceResponse<any>>>>;
  deleteFunction?: (
    id: number
  ) => Promise<Partial<AxiosResponse<ServiceResponse<any>>>>;
  defaultParams?: IParamsFilter;
  addForm?: UseFormReturn<any, any>;
  editForm?: UseFormReturn<any, any>;
  message?: {
    CREATE_SUCCESS?: string;
    CREATE_ERROR?: string;
    UPDATE_SUCCESS?: string;
    UPDATE_ERROR?: string;
    DELETE_SUCCESS?: string;
    DELETE_ERROR?: string;
    LIST_ERROR?: string;
  };
  closeAddEditModal?: () => void;
  closeDeleteModal?: () => void;
  createResponseCb?: (response: any) => Promise<any>;
  updateResponseCb?: () => Promise<any> | void;
  getExport?: () => Promise<
    Partial<AxiosResponse<ServiceResponse<PagedList<any>>>>
  >;
  exportFormatter?: (data: unknown) => unknown;
}

export const useCatalog = ({
  getKey,
  getFunction,
  createFunction,
  updateFunction,
  deleteFunction,
  closeAddEditModal,
  closeDeleteModal,
  message,
  addForm,
  editForm,
  defaultParams,
  enabled = true,
  createResponseCb,
  updateResponseCb,
  getExport,
  exportFormatter = null
}: Props) => {
  // Este setSelectOption es para que no se quede seleccionado el botón de exportar si llega a fallar la api
  const { showError, showSuccess, setSelectedOption } = useGlobalContext();
  const { queryProps, paginationProps } = usePagination();
  const queryClient = useQueryClient();
  const { t } = useTranslation('global');

  const [itemsToExport, setItemsToExport] = useState<
    object & { enabled?: boolean | string; [key: string]: unknown }[]
  >([]);

  const [params, setParams] = useState<UseCatalogParams>(
    defaultParams
      ? { ...defaultParams }
      : {
          orderColumn: 'name',
          descending: false
        }
  );

  const getResponse =
    typeof getFunction === 'function'
      ? useQuery(
          [getKey(), queryProps, params],
          () =>
            getFunction({
              ...queryProps,
              ...params
            }),
          {
            select: (data) => data?.data?.data || data?.data,
            enabled,
            onError: ({ response }) => {
              if (response?.data?.status === 403) {
                showError(t('common.commonMessages.genericPermsError'));
              } else {
                showError(
                  response?.data?.message ||
                    t('common.commonMessages.genericRequestError')
                );
              }
            },
            refetchOnWindowFocus: false
          }
        )
      : null;

  const createResponse = useMutation(createFunction, {
    onSuccess: async (response) => {
      if (typeof createResponseCb === 'function') {
        await createResponseCb(response);
      }
      queryClient.invalidateQueries(getKey());
      addForm?.reset();
      closeAddEditModal();
      showSuccess(message?.CREATE_SUCCESS);
    },
    refetchOnWindowFocus: false,
    onError: ({ response }) =>
      showError(response?.data?.message || message?.CREATE_ERROR)
  });

  const updateResponse = useMutation(updateFunction, {
    onSuccess: async (response) => {
      if (typeof updateResponseCb === 'function') {
        await updateResponseCb();
      }
      queryClient.invalidateQueries(getKey());
      editForm?.reset();
      closeAddEditModal();
      showSuccess(message?.UPDATE_SUCCESS || response?.data?.data?.message);
    },
    refetchOnWindowFocus: false,
    onError: ({ response }) =>
      showError(response?.data?.message || message?.UPDATE_ERROR)
  });

  const deleteResponse = useMutation(deleteFunction, {
    onSuccess: async (response) => {
      await queryClient.invalidateQueries(getKey());
      showSuccess(message?.DELETE_SUCCESS || response?.data?.data?.message);
      closeDeleteModal();
    },
    onError: ({ response }) => {
      if (response) {
        showError(response?.data?.message || message?.DELETE_ERROR);
      }
    }
  });

  const { refetch, isLoading: isLoadingExport } = useQuery(
    [getKey(), 'export'],
    getExport,
    {
      select: ({ data: { data } }) => data?.items,
      enabled: false,
      onSuccess: (data) => {
        let dataExport;

        if (exportFormatter) {
          dataExport = data?.map(exportFormatter);
        } else {
          dataExport = data?.map(
            (item: object & { enabled?: boolean | string }) => ({
              ...item,
              enabled: item?.enabled
                ? t('common.commonProperties.active')
                : t('common.commonProperties.inactive'),
              closedDate: item?.closedDate
                ? dayjs
                    .utc(item?.closedDate)
                    .local()
                    .format('DD/MM/YYYY HH:mm:ss')
                : '',
              loadDate: item?.loadDate
                ? dayjs
                    .utc(item?.loadDate)
                    .local()
                    .format('DD/MM/YYYY HH:mm:ss')
                : '',
              sentDate: item?.sentDate
                ? dayjs
                    .utc(item?.sentDate)
                    .local()
                    .format('DD/MM/YYYY HH:mm:ss')
                : ''
            })
          );
        }
        setItemsToExport(dataExport);
      },
      onError: ({ response }) => {
        showError(
          response?.data?.message ||
            t('common.commonMessages.genericRequestError')
        );
        // Este selectOption es para que no se quede seleccionado el botón de exportar si llega a fallar la api
        setSelectedOption(undefined);
      },
      refetchOnWindowFocus: false
    }
  );

  const handleExport = () => refetch();

  return {
    setParams,
    params,
    queryProps,
    paginationProps,
    getResponse,
    createResponse,
    updateResponse,
    deleteResponse,
    itemsToExport,
    handleExport,
    setItemsToExport,
    isLoadingExport
  };
};
